/**
 * ProjectCostTabs.jsx - a project's Tracking tab (10/14/26; it replaces the
 * five tabs Budget, Time & cost, Expenses, Equipment & Materials and COGS of
 * 10/12/26 - Sharif: "the 4 new tabs were still confusing"). One tab, four
 * views over ONE load (GET /api/project-costs/:id - the tracking master
 * Rev 1.7.4 worked out by the server):
 *   Summary                 the default: what needs attention, then the
 *                           Finance or the Project-management lens
 *   Log time & expenses     a week per person, E-sheet style; receipts; paste
 *   Materials               Equipment & Materials lines
 *   Setup                   checklist: budget, PO lines, people & rates
 * A short "How tracking works" sits on top until the person closes it (kept
 * per user on this computer).
 *
 * What a person sees is decided by the server: without 'cost-rates' the
 * answer has no cost, burden, COGS or margin in it, and the views leave
 * those panels out (never a $0 in their place). Writes need Projects Edit
 * (`editable`). Main tab only - GLOBAL KSA has its Costs & profit.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjectCosts } from '../costs/costApi';
import { GREEN, MUTED } from '../quotes/quoteUi';
import { checklistOf, isoOf, warningsOf } from './trackingInsights';
import { Segmented } from './trackingUi';
import SummaryView from './SummaryView';
import LogView from './LogView';
import MaterialsTab from './MaterialsTab';
import SetupView from './SetupView';

/** The record editor's tab id(s) this component draws. */
export const COST_TABS = ['tracking'];
export const TRACKING_VIEWS = ['summary', 'log', 'materials', 'setup'];
const HELP_KEY = (who) => `globalops_tracking_help_closed:${String(who || 'anyone').toLowerCase()}`;

function readClosed(who) {
  try {
    return window.localStorage.getItem(HELP_KEY(who)) === '1';
  } catch {
    return false;
  }
}

export function HowTrackingWorks({ onClose, go }) {
  const step = (n, title, text, target, action) => (
    <li style={{ flex: '1 1 200px', display: 'flex', gap: 10 }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 24px',
          height: 24,
          borderRadius: 12,
          background: GREEN,
          color: '#fff',
          fontWeight: 900,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 12, color: '#1F2328', lineHeight: 1.45 }}>
        <b>{title}</b> {text}{' '}
        <button
          type="button"
          onClick={() => go(target)}
          style={{
            border: 0,
            background: 'none',
            padding: 0,
            color: GREEN,
            fontWeight: 800,
            fontSize: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {action} &rarr;
        </button>
      </span>
    </li>
  );
  return (
    <section
      aria-label="How tracking works"
      style={{ background: '#F1F8E9', border: '1px solid #C8E6C9', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 800, color: GREEN, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          How tracking works
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close how tracking works"
          style={{ border: 0, background: 'none', color: MUTED, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Got it, hide this
        </button>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {step(
          1,
          'Set it up once.',
          'The budget (from the won quote), the PO lines, and the people on the job with their rates.',
          'setup:budget',
          'Setup'
        )}
        {step(
          2,
          'Log every week.',
          "Each person's hours per day (NT / OT / DT), the task, the PO line, and the expense days. Receipts and materials as they come.",
          'log',
          'Log time & expenses'
        )}
        {step(
          3,
          'Read the Summary.',
          'Finance: As sold, cost, margin, what is invoiced. Project management: hours and budget used, the burn and when the budget runs out.',
          'summary',
          'Summary'
        )}
      </ol>
    </section>
  );
}

export default function ProjectCostTabs({
  project,
  editable,
  seesCosts,
  canSeeInvoices,
  userKey,
  today: todayProp,
  initialView = 'summary',
}) {
  const id = project && project.id;
  const [state, setState] = useState({ phase: 'loading' });
  const [view, setView] = useState(TRACKING_VIEWS.includes(initialView) ? initialView : 'summary');
  const [step, setStep] = useState('budget');
  const [helpClosed, setHelpClosed] = useState(() => readClosed(userKey));
  const dirty = useRef(false);
  const onDirty = useCallback((v) => {
    dirty.current = !!v;
  }, []);
  const today = todayProp || isoOf(new Date());

  const load = useCallback(async () => {
    const r = await getProjectCosts(id);
    if (!r.ok || !r.body) {
      setState({ phase: 'failed', error: (r.body && r.body.error) || 'Could not load the project tracking.' });
      return;
    }
    setState({ phase: 'ready', data: r.body });
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  /** 'summary' | 'log' | 'materials' | 'setup' | 'setup:<step>' - asks first when there are unsaved changes. */
  const go = useCallback((target) => {
    const [v, s] = String(target).split(':');
    if (dirty.current && !window.confirm('You have unsaved changes here. Leave without saving them?')) return;
    dirty.current = false;
    setView(v);
    if (s) setStep(s);
    if (typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        /* jsdom */
      }
    }
  }, []);

  if (state.phase === 'loading') return <div style={{ padding: 30, color: '#888', fontSize: 12 }}>Loading the tracking...</div>;
  if (state.phase === 'failed')
    return (
      <div role="alert" style={{ padding: 30, color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
        {state.error}
      </div>
    );
  const d = state.data;
  // the server's word on costs wins over what the screen thinks
  const costs = !!(d.seesCosts && seesCosts !== false);
  const props = {
    data: d,
    projectId: id,
    editable,
    seesCosts: costs,
    reload: load,
    setData: (next) => setState({ phase: 'ready', data: next }),
    onDirty,
    go,
    today,
  };
  const warn = warningsOf(d, costs).length;
  const todo = checklistOf(d, costs).filter((s) => s.state !== 'done').length;
  const closeHelp = () => {
    setHelpClosed(true);
    try {
      window.localStorage.setItem(HELP_KEY(userKey), '1');
    } catch {
      /* a convenience only */
    }
  };

  return (
    <div>
      {!helpClosed && <HowTrackingWorks onClose={closeHelp} go={go} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Segmented
          label="Tracking views"
          value={view}
          onChange={(v) => go(v)}
          options={[
            { value: 'summary', label: 'Summary', badge: warn || null, badgeTone: '#C62828' },
            { value: 'log', label: 'Log time & expenses' },
            { value: 'materials', label: 'Materials' },
            { value: 'setup', label: 'Setup', badge: todo || null },
          ]}
        />
        {helpClosed && (
          <button
            type="button"
            onClick={() => setHelpClosed(false)}
            style={{
              border: 0,
              background: 'none',
              color: MUTED,
              fontSize: 11.5,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginLeft: 'auto',
            }}
          >
            How tracking works
          </button>
        )}
      </div>
      {view === 'summary' && <SummaryView {...props} canSeeInvoices={canSeeInvoices} />}
      {view === 'log' && <LogView {...props} />}
      {view === 'materials' && <MaterialsTab {...props} />}
      {view === 'setup' && <SetupView {...props} step={step} setStep={(s) => go(`setup:${s}`)} />}
    </div>
  );
}
